import { caminhoDoModulo, filhosDe, interceptar } from "./navegacaoMapa";
import type {
  GrupoDeNavegacao,
  ItemDeNavegacao,
  SubmoduloDeNavegacao,
} from "./navegacaoMapa";

import "./NavegacaoArvore.css";

/**
 * A ÁRVORE de destinos — a navegação <b>esticada</b>, nos três níveis.
 *
 * <pre>
 * Produção/                    ← setor    (grupo)
 *   Matérias-primas/           ← módulo   (item COM tela própria e filhos)
 *     Estoque e movimentação   ← submódulo
 *     Dispensação e pesagem    ← submódulo
 * </pre>
 *
 * <p>O terceiro nível existe porque estoque e dispensação são operações
 * <b>sobre</b> matéria-prima, não vizinhas dela: como irmãos de
 * "Matérias-primas" eles sugeriam três cadastros independentes. O eixo desta
 * lista é <b>o que a pessoa procura</b>, e quem procura o estoque de
 * matéria-prima procura dentro de matérias-primas.
 *
 * <p>O nível para no terceiro <b>por tipo</b>, não por convenção: o filho é um
 * {@link SubmoduloDeNavegacao}, que não tem `itens`. Um mapa recursivo aceitaria
 * um quarto recuo que nem o CSS da gaveta nem a leitura de relance sustentam.
 *
 * <h2>Isto não é controle de acesso, e não pode parecer</h2>
 *
 * <p>O item indisponível <b>não é um link desabilitado</b>: ele não é link
 * nenhum, não recebe foco de teclado e não tem destino. Não há rota
 * desprotegida atrás dele porque não há rota. Ver `Navegacao.tsx`.
 *
 * <h2>Módulo com tela própria: LINK e BOTÃO, lado a lado</h2>
 *
 * <p>"Matérias-primas" tem rota <b>e</b> filhos, e um único elemento não
 * consegue ser as duas coisas: virando `<button>` de expandir perde-se o
 * destino; virando só `<a>` perde-se o expandir. Um clique que às vezes navega
 * e às vezes abre a lista é pior que os dois: quem toca o nome esperando a tela
 * recebe uma lista, e desaprende a confiar no menu.
 *
 * <p>São <b>dois controles</b>, um ao lado do outro, cada um com seu alvo de
 * 2.75rem: o nome leva à tela, a seta abre a lista. A divisória entre eles é
 * visível de propósito — alvo que não se enxerga é alvo que se erra de luva.
 *
 * <p>Este arquivo saiu de `Navegacao.tsx` (804 linhas) pelo teto de 300 do
 * ADR-0013 §2, e o corte é o que o próprio ADR pediu: <b>coluna · árvore ·
 * faixa recolhida</b>. Nada aqui mudou de comportamento na saída.
 */

/** O que os três níveis precisam saber para se desenhar. */
export interface Cascata {
  rotaAtual: string;
  aoNavegar(rota: string): void;
  estaAberto(caminho: string): boolean;
  alternar(caminho: string): void;
  prefixo: string;
}

export function Grupo({
  grupo,
  ...cascata
}: {
  grupo: GrupoDeNavegacao;
} & Cascata) {
  const { estaAberto, alternar, prefixo } = cascata;
  const temAlgumaTela = grupo.itens.some(
    (i) => i.rota !== null || filhosDe(i).some((f) => f.rota !== null),
  );
  const classeDoTitulo = `ui-navegacao__titulo${temAlgumaTela ? "" : " ui-navegacao__titulo--inerte"}`;

  /*
   * Setor sem item NÃO é tópico — é aviso.
   *
   * Qualidade tem `itens: []` e só uma observação. Um cabeçalho que expande
   * para uma linha de texto e recolhe para nada promete navegação que não
   * existe: quem clica espera destino e recebe explicação. Pior, a explicação
   * é o que impede o título sozinho de se ler como "você não tem acesso" — e
   * ela é justamente o que ficaria escondido atrás do clique.
   *
   * Então o título continua título, e a observação fica sempre à vista.
   */
  if (grupo.itens.length === 0) {
    return (
      <section className="ui-navegacao__grupo">
        {/* `--sozinho` porque o recuo que alinha os outros títulos mora no
            botão que este não tem. */}
        <h2 className={`${classeDoTitulo} ui-navegacao__titulo--sozinho`}>{grupo.rotulo}</h2>

        {grupo.observacao !== undefined && (
          <p className="ui-navegacao__observacao">{grupo.observacao}</p>
        )}
      </section>
    );
  }

  const aberto = estaAberto(grupo.id);
  const idDosItens = `${prefixo}${grupo.id}`;

  return (
    <section className="ui-navegacao__grupo">
      <h2 className={classeDoTitulo}>
        {/* `<button>`, não `<div>` com onClick: div clicável não entra no
            caminho do TAB nem é anunciada como controle, e esta gaveta é
            operada por teclado. Quem diz o estado é o `aria-expanded`. */}
        <button
          type="button"
          className="ui-navegacao__cabecalho"
          aria-expanded={aberto}
          aria-controls={idDosItens}
          onClick={() => alternar(grupo.id)}
        >
          <span className="ui-navegacao__rotulo">{grupo.rotulo}</span>

          {/* A seta é para o olho. `aria-hidden` porque o estado já foi dito
              pelo `aria-expanded` — repetir seria anunciar duas vezes a mesma
              coisa, uma delas em forma de desenho. */}
          <span className="ui-navegacao__seta" aria-hidden="true" />
        </button>
      </h2>

      {grupo.observacao !== undefined && (
        <p className="ui-navegacao__observacao">{grupo.observacao}</p>
      )}

      {/* `hidden` em vez de não desenhar: o `aria-controls` do botão precisa
          apontar para algo que exista, e o atributo tira a lista do caminho do
          TAB e da árvore de acessibilidade de uma vez. */}
      <ul id={idDosItens} hidden={!aberto} className="ui-navegacao__itens">
        {grupo.itens.map((item) => (
          <Item key={item.id} item={item} caminho={caminhoDoModulo(grupo, item)} {...cascata} />
        ))}
      </ul>
    </section>
  );
}

/**
 * Um item do segundo nível — folha, ou módulo com filhos.
 *
 * <p>Só ganha botão de disclosure quem tem o que abrir. Interruptor ao lado de
 * um item sem filho prometeria conteúdo e entregaria o mesmo lugar — e ainda
 * gastaria 44px de largura numa gaveta de 256.
 */
function Item({
  item,
  caminho,
  ...cascata
}: {
  item: ItemDeNavegacao;
  /** `setor/modulo` — chave do recolhimento e base do `id` do `aria-controls`. */
  caminho: string;
} & Cascata) {
  const filhos = filhosDe(item);
  const { rotaAtual, aoNavegar, estaAberto, alternar, prefixo } = cascata;

  if (filhos.length === 0) {
    return <Folha item={item} rotaAtual={rotaAtual} aoNavegar={aoNavegar} />;
  }

  const aberto = estaAberto(caminho);
  const idDosSubitens = `${prefixo}${caminho}`;

  return (
    <li className="ui-navegacao__item ui-navegacao__item--modulo">
      {/*
        DOIS controles, não um. "Matérias-primas" é destino E é tópico, e um
        elemento só não consegue ser as duas coisas sem tornar o toque uma
        aposta: tocar o nome vai para a tela, tocar a seta abre a lista.
      */}
      <div className="ui-navegacao__linha">
        {item.rota === null ? (
          // Módulo cuja tela ainda não existe, mas cujos filhos já são
          // conhecidos. Continua sem ser link — não há rota desprotegida
          // atrás dele porque não há rota —, e o disclosure ao lado continua
          // funcionando: a lista de baixo é o que ele tem a oferecer hoje.
          <span className="ui-navegacao__nome ui-navegacao__nome--emConstrucao">
            <span>{item.rotulo}</span>
            <span className="ui-navegacao__marca">em construção</span>
          </span>
        ) : (
          <Ligacao
            rotulo={item.rotulo}
            rota={item.rota}
            rotaAtual={rotaAtual}
            aoNavegar={aoNavegar}
            classeExtra="ui-navegacao__nome"
          />
        )}

        {/*
          O nome do botão NÃO é o do módulo: dois controles com o mesmo nome,
          um ao lado do outro, é a ambiguidade de volta na árvore de
          acessibilidade — quem ouve a tela não teria como saber qual leva
          aonde. E o estado fica por conta do `aria-expanded`, uma vez só.
        */}
        <button
          type="button"
          className="ui-navegacao__disclosure"
          aria-expanded={aberto}
          aria-controls={idDosSubitens}
          aria-label={`Itens de ${item.rotulo}`}
          onClick={() => alternar(caminho)}
        >
          <span className="ui-navegacao__seta" aria-hidden="true" />
        </button>
      </div>

      {/* Mesmo contrato do segundo nível: `hidden` tira do TAB e da árvore de
          acessibilidade. A folha de estilo só acompanha. */}
      <ul
        id={idDosSubitens}
        hidden={!aberto}
        className="ui-navegacao__itens ui-navegacao__subitens"
      >
        {filhos.map((filho) => (
          <Folha key={filho.id} item={filho} rotaAtual={rotaAtual} aoNavegar={aoNavegar} />
        ))}
      </ul>
    </li>
  );
}

/** Linha sem filhos — no segundo ou no terceiro nível, o desenho é o mesmo. */
function Folha({
  item,
  rotaAtual,
  aoNavegar,
}: {
  item: ItemDeNavegacao | SubmoduloDeNavegacao;
  rotaAtual: string;
  aoNavegar(rota: string): void;
}) {
  if (item.rota === null) {
    // Sem <a> e sem <button>: não é destino, não é foco de teclado,
    // e não há rota desprotegida atrás dele porque não há rota.
    return (
      <li className="ui-navegacao__item ui-navegacao__item--emConstrucao">
        <span>{item.rotulo}</span>
        <span className="ui-navegacao__marca">em construção</span>
      </li>
    );
  }

  return (
    <li className="ui-navegacao__item">
      <Ligacao
        rotulo={item.rotulo}
        rota={item.rota}
        rotaAtual={rotaAtual}
        aoNavegar={aoNavegar}
      />
    </li>
  );
}

function Ligacao({
  rotulo,
  rota,
  rotaAtual,
  aoNavegar,
  classeExtra,
}: {
  rotulo: string;
  rota: string;
  rotaAtual: string;
  aoNavegar(rota: string): void;
  classeExtra?: string;
}) {
  const ativo = rota === rotaAtual;
  const classes = [classeExtra, ativo ? "ui-navegacao__ativo" : undefined]
    .filter((c): c is string => c !== undefined)
    .join(" ");

  return (
    <a
      href={rota}
      aria-current={ativo ? "page" : undefined}
      className={classes === "" ? undefined : classes}
      onClick={interceptar(rota, aoNavegar)}
    >
      {rotulo}
    </a>
  );
}
